import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatPrice } from "@/lib/currency";
import { Tag, Truck, ShieldCheck, Check, Lock } from "lucide-react";

const COUNTRIES = ["United Arab Emirates", "Saudi Arabia", "Qatar", "Kuwait", "Oman", "Bahrain", "United Kingdom", "United States", "France", "Other"];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    shipping_name: user?.name || "",
    shipping_email: user?.email || "",
    shipping_address: "",
    shipping_city: "",
    shipping_country: "United Arab Emirates",
    shipping_phone: "",
    notes: "",
  });
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [placing, setPlacing] = useState(false);

  if (items.length === 0) return <Navigate to="/cart" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyCoupon = async () => {
    if (!coupon) return;
    try {
      const { data } = await api.get(`/coupons/validate/${coupon.toUpperCase()}`);
      setDiscount(subtotal * (data.discount_percent / 100));
      setAppliedCoupon(data);
      toast.success(`Coupon ${data.code} applied — ${data.discount_percent}% off`);
    } catch {
      toast.error("Invalid coupon");
      setDiscount(0); setAppliedCoupon(null);
    }
  };

  const removeCoupon = () => { setAppliedCoupon(null); setDiscount(0); setCoupon(""); };

  const place = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size, color: i.color })),
        ...form,
        coupon_code: appliedCoupon?.code || null,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      toast.success("Order placed successfully");
      nav(`/order/${data.id}`);
    } catch {
      toast.error("Could not place order");
    } finally { setPlacing(false); }
  };

  const shipping = subtotal > 200 ? 0 : 25;
  const tax = (subtotal - discount) * 0.05; // 5% VAT
  const total = subtotal - discount + shipping + tax;

  return (
    <div data-testid="checkout-page" className="bg-[#F5F5F0] min-h-screen">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16">
        <div className="text-center mb-14">
          <div className="overline text-[#C5A059]">Secure checkout</div>
          <h1 className="serif text-5xl lg:text-6xl mt-3">Almost yours.</h1>
          <p className="text-[#5A5A5A] mt-3 max-w-md mx-auto text-sm">
            Two quiet sections — your details and your delivery. We handle the rest.
          </p>
        </div>

        <form onSubmit={place} className="grid lg:grid-cols-5 gap-10">
          {/* Left — form */}
          <div className="lg:col-span-3 space-y-10">
            {/* Contact */}
            <section className="bg-white p-7 lg:p-9 border border-[#E5E5DF]">
              <div className="flex items-center gap-3 mb-7">
                <span className="w-7 h-7 rounded-full bg-[#0A0A0A] text-white text-xs flex items-center justify-center">1</span>
                <h2 className="serif text-2xl">Your details</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <div className="fld-label">Full name</div>
                  <input data-testid="checkout-name" required value={form.shipping_name} onChange={(e) => set("shipping_name", e.target.value)} className="fld mt-2" placeholder="Layla Al-Khalifa" />
                </div>
                <div>
                  <div className="fld-label">Email</div>
                  <input data-testid="checkout-email" type="email" required value={form.shipping_email} onChange={(e) => set("shipping_email", e.target.value)} className="fld mt-2" placeholder="you@email.com" />
                </div>
                <div className="sm:col-span-2">
                  <div className="fld-label">Phone</div>
                  <input data-testid="checkout-phone" required value={form.shipping_phone} onChange={(e) => set("shipping_phone", e.target.value)} className="fld mt-2" placeholder="+971 50 000 0000" />
                </div>
              </div>
            </section>

            {/* Delivery */}
            <section className="bg-white p-7 lg:p-9 border border-[#E5E5DF]">
              <div className="flex items-center gap-3 mb-7">
                <span className="w-7 h-7 rounded-full bg-[#0A0A0A] text-white text-xs flex items-center justify-center">2</span>
                <h2 className="serif text-2xl">Delivery</h2>
              </div>
              <div className="space-y-6">
                <div>
                  <div className="fld-label">Address</div>
                  <input data-testid="checkout-address" required value={form.shipping_address} onChange={(e) => set("shipping_address", e.target.value)} className="fld mt-2" placeholder="Apartment, street" />
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <div className="fld-label">City / Emirate</div>
                    <input data-testid="checkout-city" required value={form.shipping_city} onChange={(e) => set("shipping_city", e.target.value)} className="fld mt-2" placeholder="Dubai" />
                  </div>
                  <div>
                    <div className="fld-label">Country</div>
                    <select data-testid="checkout-country" value={form.shipping_country} onChange={(e) => set("shipping_country", e.target.value)} className="fld mt-2">
                      {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <div className="fld-label">Order notes (optional)</div>
                  <input data-testid="checkout-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="fld mt-2" placeholder="Anything we should know" />
                </div>
              </div>

              <div className="mt-7 flex items-center gap-3 text-xs text-[#5A5A5A] bg-[#EFEFE8] px-4 py-3">
                <Truck size={14} className="text-[#C5A059]" />
                Express UAE delivery in 1–2 days · International 3–7 days
              </div>
            </section>

            {/* Payment */}
            <section className="bg-white p-7 lg:p-9 border border-[#E5E5DF]">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-7 h-7 rounded-full bg-[#0A0A0A] text-white text-xs flex items-center justify-center">3</span>
                <h2 className="serif text-2xl">Payment</h2>
              </div>
              <div className="text-sm text-[#5A5A5A] leading-relaxed">
                Demo checkout — no card is charged. In production this connects to a secure gateway with Apple Pay, card, and cash on delivery for UAE orders.
              </div>
              <div className="mt-5 flex flex-wrap gap-2 text-[0.65rem] tracking-[0.2em] uppercase">
                <span className="border border-[#E5E5DF] px-3 py-2">Card</span>
                <span className="border border-[#E5E5DF] px-3 py-2">Apple Pay</span>
                <span className="border border-[#E5E5DF] px-3 py-2">Cash on delivery</span>
              </div>
            </section>
          </div>

          {/* Right — summary */}
          <aside className="lg:col-span-2">
            <div className="bg-white border border-[#E5E5DF] p-7 lg:sticky lg:top-44">
              <div className="overline">Your order</div>

              <div className="mt-5 space-y-4 max-h-72 overflow-auto pr-1">
                {items.map((it) => (
                  <div key={it.key} className="flex gap-3">
                    <div className="relative">
                      <img src={it.image} alt="" className="w-14 h-18 object-cover" />
                      <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#0A0A0A] text-white text-[10px] rounded-full flex items-center justify-center">{it.quantity}</span>
                    </div>
                    <div className="flex-1 text-sm">
                      <div className="serif text-base leading-tight">{it.name}</div>
                      <div className="text-[10px] text-[#5A5A5A] mt-0.5 tracking-wide">{[it.size, it.color].filter(Boolean).join(" · ")}</div>
                    </div>
                    <div className="text-xs whitespace-nowrap">{formatPrice(it.price * it.quantity)}</div>
                  </div>
                ))}
              </div>

              {/* Coupon */}
              <div className="mt-6 pt-6 border-t border-[#E5E5DF]">
                {appliedCoupon ? (
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 text-[#C5A059]">
                      <Tag size={13} /> {appliedCoupon.code} · −{appliedCoupon.discount_percent}%
                    </span>
                    <button type="button" onClick={removeCoupon} className="underline">Remove</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      data-testid="coupon-input"
                      value={coupon}
                      onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                      placeholder="Promo code"
                      className="fld text-xs"
                    />
                    <button type="button" data-testid="apply-coupon" onClick={applyCoupon} className="text-[0.7rem] uppercase tracking-[0.2em] px-3 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors">
                      Apply
                    </button>
                  </div>
                )}
                <div className="text-[10px] text-[#8A8A8A] mt-2">Try WELCOME10 or ATELIER20</div>
              </div>

              {/* Totals */}
              <div className="mt-6 pt-6 border-t border-[#E5E5DF] space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#5A5A5A]">Subtotal</span><span>{formatPrice(subtotal)}</span></div>
                {discount > 0 && (
                  <div className="flex justify-between text-[#C5A059]"><span>Discount</span><span>−{formatPrice(discount)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-[#5A5A5A]">Shipping</span><span>{shipping === 0 ? "Free" : formatPrice(shipping)}</span></div>
                <div className="flex justify-between"><span className="text-[#5A5A5A]">VAT (5%)</span><span>{formatPrice(tax)}</span></div>
              </div>
              <div className="flex justify-between items-baseline mt-5 pt-5 border-t border-[#E5E5DF]">
                <span className="overline">Total</span>
                <span className="serif text-3xl" data-testid="checkout-total">{formatPrice(total)}</span>
              </div>

              <button
                data-testid="place-order-button"
                disabled={placing}
                className="btn-primary w-full justify-center mt-7 group"
              >
                {placing ? "Placing your order…" : <><Lock size={13} /> Place order</>}
              </button>

              <div className="mt-5 grid grid-cols-2 gap-3 text-[10px] text-[#5A5A5A]">
                <div className="flex items-center gap-2"><ShieldCheck size={13} className="text-[#C5A059]" /> Secure checkout</div>
                <div className="flex items-center gap-2"><Check size={13} className="text-[#C5A059]" /> 30-day returns</div>
              </div>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
}
